import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Power, PowerOff } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "../payroll.css";

const initialForm = {
  component_code: "",
  component_name: "",
  component_type: "earning",
  amount_type: "fixed",
  default_amount: "",
  default_percentage: "",
  applies_to: "basic",
  applies_to_components: [],
  taxable: true,
  pensionable: false,
  active: true,
  sequence_no: 1,
  description: "",
};

function cleanCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function numberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export default function PayrollComponents() {
  const { companyId } = useCompany();

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(initialForm);

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const { data, error } = await supabase
        .from("payroll_pay_components")
        .select("*")
        .eq("company_id", companyId)
        .order("sequence_no", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) throw error;

      setRows(data || []);
    } catch (err) {
      setMsg(err.message || "Failed to load payroll components");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const availableDependencyComponents = useMemo(() => {
    return (rows || []).filter((row) => {
      const code = String(row.component_code || "").toUpperCase();
      if (editingRow?.id && row.id === editingRow.id) return false;
      return code !== "BASE";
    });
  }, [rows, editingRow]);

  function resetForm() {
    setForm(initialForm);
    setEditingRow(null);
  }

  function openCreate() {
    resetForm();
    setOpenModal(true);
  }

  function openEdit(row) {
    setEditingRow(row);
    setForm({
      component_code: row.component_code || "",
      component_name: row.component_name || "",
      component_type: row.component_type || "earning",
      amount_type: row.amount_type || "fixed",
      default_amount:
        row.default_amount === null || row.default_amount === undefined
          ? ""
          : row.default_amount,
      default_percentage:
        row.default_percentage === null || row.default_percentage === undefined
          ? ""
          : row.default_percentage,
      applies_to: row.applies_to || "basic",
      applies_to_components: Array.isArray(row.applies_to_components)
        ? row.applies_to_components
        : [],
      taxable: row.taxable ?? true,
      pensionable: row.pensionable ?? false,
      active: row.active ?? true,
      sequence_no: row.sequence_no ?? 1,
      description: row.description || "",
    });
    setOpenModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!companyId) return;

    try {
      setSaving(true);
      setMsg("");

      const payload = {
        company_id: companyId,
        component_code: cleanCode(form.component_code),
        component_name: String(form.component_name || "").trim(),
        component_type: form.component_type,
        amount_type: form.amount_type,
        default_amount:
          form.amount_type === "fixed"
            ? numberOrNull(form.default_amount)
            : null,
        default_percentage:
          form.amount_type === "percentage"
            ? numberOrNull(form.default_percentage)
            : null,
        applies_to: form.amount_type === "percentage" ? form.applies_to : null,
        applies_to_components:
          form.amount_type === "percentage" && form.applies_to === "custom"
            ? form.applies_to_components
            : null,
        taxable: !!form.taxable,
        pensionable: !!form.pensionable,
        active: !!form.active,
        sequence_no: Number(form.sequence_no || 1),
        description: form.description || null,
      };

      if (!payload.component_code) {
        throw new Error("Component code is required");
      }

      if (!payload.component_name) {
        throw new Error("Component name is required");
      }

      if (
        payload.amount_type === "percentage" &&
        (payload.default_percentage === null ||
          Number(payload.default_percentage) < 0)
      ) {
        throw new Error("Enter a valid default percentage");
      }

      if (
        payload.amount_type === "percentage" &&
        payload.applies_to === "custom" &&
        (!payload.applies_to_components ||
          !payload.applies_to_components.length)
      ) {
        throw new Error("Select at least one dependency component");
      }

      if (editingRow?.id) {
        const { error } = await supabase
          .from("payroll_pay_components")
          .update(payload)
          .eq("id", editingRow.id)
          .eq("company_id", companyId);

        if (error) throw error;
        setMsg("Payroll component updated successfully.");
      } else {
        const { error } = await supabase
          .from("payroll_pay_components")
          .insert(payload);

        if (error) throw error;
        setMsg("Payroll component created successfully.");
      }

      setOpenModal(false);
      resetForm();
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to save payroll component");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(row) {
    try {
      setMsg("");
      const { error } = await supabase
        .from("payroll_pay_components")
        .update({ active: !row.active })
        .eq("id", row.id)
        .eq("company_id", companyId);

      if (error) throw error;
      setMsg(
        `${row.component_name} has been ${
          row.active ? "deactivated" : "activated"
        }.`
      );
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to update component status");
    }
  }

  async function handleDelete(row) {
    const ok = window.confirm(
      `Delete "${row.component_name}"? This cannot be undone.`
    );
    if (!ok) return;

    try {
      setMsg("");
      const { error } = await supabase
        .from("payroll_pay_components")
        .delete()
        .eq("id", row.id)
        .eq("company_id", companyId);

      if (error) throw error;
      setMsg("Payroll component deleted successfully.");
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to delete payroll component");
    }
  }

  function toggleDependency(code) {
    setForm((prev) => {
      const exists = prev.applies_to_components.includes(code);
      return {
        ...prev,
        applies_to_components: exists
          ? prev.applies_to_components.filter((x) => x !== code)
          : [...prev.applies_to_components, code],
      };
    });
  }

  const summary = useMemo(() => {
    const data = rows || [];
    return {
      total: data.length,
      earnings: data.filter((x) => x.component_type === "earning").length,
      deductions: data.filter((x) => x.component_type === "deduction").length,
      employer: data.filter((x) => x.component_type === "employer_contribution")
        .length,
      active: data.filter((x) => x.active).length,
    };
  }, [rows]);

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Components</p>
          <h3>{summary.total}</h3>
          <span>Total payroll components</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Earnings</p>
          <h3>{summary.earnings}</h3>
          <span>Earnings and allowances</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Deductions</p>
          <h3>{summary.deductions}</h3>
          <span>Deductions and employee costs</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Employer Contrib.</p>
          <h3>{summary.employer}</h3>
          <span>Employer-only components</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Payroll Components</h3>
            <p>
              Create fixed or percentage-based payroll components, including
              dependency-driven components like Basic + Housing + Transport.
            </p>
          </div>

          <div className="table-actions">
            <button type="button" className="primary-btn" onClick={openCreate}>
              <Plus size={16} />
              Add Component
            </button>
          </div>
        </div>

        <div className="payroll-alert info" style={{ marginBottom: 12 }}>
          For percentage-based components, you can apply to Basic, Gross,
          Taxable Gross, Net, or a custom sum of selected components.
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Amount Type</th>
                <th>Default</th>
                <th>Applies To</th>
                <th>Dependencies</th>
                <th>Taxable</th>
                <th>Pensionable</th>
                <th>Status</th>
                <th>Seq</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((row) => (
                <tr key={row.id}>
                  <td>{row.component_code}</td>
                  <td>{row.component_name}</td>
                  <td>{row.component_type}</td>
                  <td>{row.amount_type}</td>
                  <td>
                    {row.amount_type === "fixed"
                      ? Number(row.default_amount || 0).toLocaleString()
                      : `${Number(
                          row.default_percentage || 0
                        ).toLocaleString()}%`}
                  </td>
                  <td>{row.applies_to || "-"}</td>
                  <td>
                    {Array.isArray(row.applies_to_components) &&
                    row.applies_to_components.length
                      ? row.applies_to_components.join(", ")
                      : "-"}
                  </td>
                  <td>{row.taxable ? "Yes" : "No"}</td>
                  <td>{row.pensionable ? "Yes" : "No"}</td>
                  <td>
                    <span
                      className={`pill ${row.active ? "approved" : "draft"}`}
                    >
                      {row.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td>{row.sequence_no ?? "-"}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={() => openEdit(row)}
                      >
                        <Pencil size={15} />
                      </button>

                      <button
                        type="button"
                        className="icon-btn"
                        title={row.active ? "Deactivate" : "Activate"}
                        onClick={() => handleToggleActive(row)}
                      >
                        {row.active ? (
                          <PowerOff size={15} />
                        ) : (
                          <Power size={15} />
                        )}
                      </button>

                      <button
                        type="button"
                        className="icon-btn danger"
                        title="Delete"
                        onClick={() => handleDelete(row)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!rows.length && !busy && (
                <tr>
                  <td colSpan="12">No payroll components found.</td>
                </tr>
              )}

              {busy && (
                <tr>
                  <td colSpan="12">Loading...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal" style={{ maxWidth: 860 }}>
            <div className="payroll-modal-header">
              <h3>
                {editingRow
                  ? "Edit Payroll Component"
                  : "Add Payroll Component"}
              </h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setOpenModal(false);
                  resetForm();
                }}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleSubmit}>
              <div className="payroll-grid payroll-grid-3">
                <label>
                  Component Code
                  <input
                    value={form.component_code}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        component_code: e.target.value,
                      }))
                    }
                    placeholder="e.g. HOUSING"
                    required
                  />
                </label>

                <label>
                  Component Name
                  <input
                    value={form.component_name}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        component_name: e.target.value,
                      }))
                    }
                    placeholder="e.g. Housing Allowance"
                    required
                  />
                </label>

                <label>
                  Component Type
                  <select
                    value={form.component_type}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        component_type: e.target.value,
                      }))
                    }
                  >
                    <option value="earning">Earning</option>
                    <option value="deduction">Deduction</option>
                    <option value="employer_contribution">
                      Employer Contribution
                    </option>
                  </select>
                </label>

                <label>
                  Amount Type
                  <select
                    value={form.amount_type}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        amount_type: e.target.value,
                        applies_to:
                          e.target.value === "percentage"
                            ? p.applies_to
                            : "basic",
                        applies_to_components:
                          e.target.value === "percentage"
                            ? p.applies_to_components
                            : [],
                      }))
                    }
                  >
                    <option value="fixed">Fixed</option>
                    <option value="percentage">Percentage</option>
                  </select>
                </label>

                {form.amount_type === "fixed" ? (
                  <label>
                    Default Amount
                    <input
                      type="number"
                      step="0.01"
                      value={form.default_amount}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          default_amount: e.target.value,
                        }))
                      }
                    />
                  </label>
                ) : (
                  <label>
                    Default Percentage (%)
                    <input
                      type="number"
                      step="0.0001"
                      value={form.default_percentage}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          default_percentage: e.target.value,
                        }))
                      }
                    />
                  </label>
                )}

                <label>
                  Sequence No
                  <input
                    type="number"
                    value={form.sequence_no}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        sequence_no: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              {form.amount_type === "percentage" ? (
                <>
                  <div className="payroll-grid payroll-grid-2">
                    <label>
                      Apply Percentage To
                      <select
                        value={form.applies_to}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            applies_to: e.target.value,
                            applies_to_components:
                              e.target.value === "custom"
                                ? p.applies_to_components
                                : [],
                          }))
                        }
                      >
                        <option value="basic">Basic Salary</option>
                        <option value="gross">Gross Salary</option>
                        <option value="taxable_gross">Taxable Gross</option>
                        <option value="net">Net</option>
                        <option value="custom">Custom Components</option>
                      </select>
                    </label>
                  </div>

                  {form.applies_to === "custom" ? (
                    <div className="payroll-card" style={{ marginTop: 10 }}>
                      <div className="payroll-card-header">
                        <div>
                          <h3 style={{ fontSize: 14 }}>
                            Select Dependency Components
                          </h3>
                          <p>
                            The percentage will be applied to the sum of the
                            selected components.
                          </p>
                        </div>
                      </div>

                      <div
                        className="payroll-grid payroll-grid-3"
                        style={{ gap: 8 }}
                      >
                        {availableDependencyComponents.map((comp) => (
                          <label
                            key={comp.id}
                            className="check-inline"
                            style={{
                              border: "1px solid #e8eeee",
                              borderRadius: 10,
                              padding: "10px 12px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={form.applies_to_components.includes(
                                comp.component_code
                              )}
                              onChange={() =>
                                toggleDependency(comp.component_code)
                              }
                            />
                            {comp.component_name} ({comp.component_code})
                          </label>
                        ))}

                        {!availableDependencyComponents.length ? (
                          <div>
                            No components available for dependency selection.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              <div
                className="payroll-grid payroll-grid-3"
                style={{ marginTop: 12 }}
              >
                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={!!form.taxable}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        taxable: e.target.checked,
                      }))
                    }
                  />
                  Taxable
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={!!form.pensionable}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        pensionable: e.target.checked,
                      }))
                    }
                  />
                  Pensionable
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={!!form.active}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>
              </div>

              <label style={{ marginTop: 12 }}>
                Description
                <textarea
                  rows="4"
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                />
              </label>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => {
                    setOpenModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>

                <button type="submit" className="primary-btn" disabled={saving}>
                  {saving
                    ? editingRow
                      ? "Updating..."
                      : "Saving..."
                    : editingRow
                    ? "Update Component"
                    : "Save Component"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
